import { formatTable, Table } from 'common/types/disco/table/table';

/**
 * TableQuerier provides an API / documentation for table querying
 */
type TableQuerier = {
  /** @returns values from the given column name */
  findColumn: (columnName: string) => string[] | undefined;
  /**
   * @returns row values from the row which has the given column name and value
   *
   * Note: this returns the row from the queried table, so mutations will affect
   * the table. If this turns out problematic, we should consider returning a copy.
   */
  findRow: (columnName: string, value: string) => string[] | undefined;
  /** @returns the column index for the given name or -1 if not found */
  columnIndices: (...columnNames: string[]) => number[];
  /** @returns a pretty formatted table or undefined if no table */
  string: () => string;
};

/**
 * queryTable provides an API for querying information about a table.
 */
export function queryTable(data?: Table): TableQuerier {
  const columnIndicesByName =
    data?.columnNames.reduce<{ [name: string]: number }>((acc, name, i) => {
      acc[name] = i;
      return acc;
    }, {}) ?? {};

  const query: TableQuerier = {
    findColumn: (columnName: string) => {
      const idx = columnIndicesByName[columnName];
      if (idx !== undefined && data) {
        return data.rows.map(row => row[idx]);
      }
      return undefined;
    },
    findRow: (columnName: string, value: string) => {
      const idx = columnIndicesByName[columnName];
      if (idx !== undefined && data) {
        return data.rows.find(row => row[idx] === value);
      }
      return undefined;
    },
    columnIndices: (...columnNames: string[]) => {
      return columnNames.map(name => columnIndicesByName[name] ?? -1);
    },
    string: () => {
      return data ? formatTable(data) : 'undefined';
    },
  };

  return query;
}
